import { Button, DropDown, Menu, MenuItem } from '@/src/components/Antd';
import { useI18n } from '@/src/hooks/useRouterChange';
import { Icon } from '@/src/tools/icon';
import { handleOnGetStoreTimezoneUTC, i18nGet } from '@/src/tools/tools';
import {
  CustomerPaymentItem,
  CustomerPaymentStatus,
} from '@/src/types/common/paymentGateway';
import React from 'react';
import {
  canRefund,
  getAppliedI18nKey,
  getGatewayLabel,
  getStatusI18nKey,
} from './config';
import css from './css.module.less';

interface PaymentItemProps {
  item: CustomerPaymentItem;
  index: number;
  currencyCode?: string;
  decimalPosition?: number;
  canProcessRefund: boolean;
  onRefund: (item: CustomerPaymentItem) => void;
}

interface PaymentFieldProps {
  label: string;
  value: React.ReactNode;
}

const EMPTY_VALUE = '-';
const THOUSANDS_SEPARATOR_REGEXP = /\B(?=(\d{3})+(?!\d))/g;

const PaymentField = ({ label, value }: PaymentFieldProps) => {
  return (
    <div className={css.field}>
      <h3>{label}</h3>
      <span>{value}</span>
    </div>
  );
};

const formatAmount = (amount: number | undefined, decimalPosition = 2) => {
  if (!Number.isFinite(amount)) {
    return EMPTY_VALUE;
  }

  return Number(amount)
    .toFixed(decimalPosition)
    .replace(THOUSANDS_SEPARATOR_REGEXP, ',');
};

const PaymentItem = ({
  item,
  index,
  currencyCode,
  decimalPosition = 2,
  canProcessRefund,
  onRefund,
}: PaymentItemProps) => {
  const i18n = useI18n();
  const getI18n = (key: string) => i18nGet(i18n, key);
  const timezoneUTC = handleOnGetStoreTimezoneUTC();
  const paymentDetail = item?.paymentDetail;
  const statusI18nKey = getStatusI18nKey(paymentDetail?.status);
  const appliedI18nKey = getAppliedI18nKey(item);
  const canProcessPaymentRefund = canProcessRefund && canRefund(item);
  const refundActionText = getI18n(
    'allOrders.orderDetail.paymentHistory_refund_action',
  );

  const getTimeLabel = (key: string) => {
    const label = getI18n(key);
    return timezoneUTC ? `${label} (UTC ${timezoneUTC})` : label;
  };

  const refundMenu = (
    <Menu>
      <MenuItem className={css.menuItem} onClick={() => onRefund(item)}>
        {refundActionText}
      </MenuItem>
    </Menu>
  );

  return (
    <div className={css.paymentItem}>
      <div className={css.header}>
        <h3>
          {getI18n('allOrders.orderDetail.paymentHistory_payment')} #{index + 1}
        </h3>
        {canProcessPaymentRefund ? (
          <DropDown
            overlay={refundMenu}
            placement='bottomRight'
            trigger={['click']}>
            <Button
              className={css.actionButton}
              icon={<Icon type='iconMore' />}
              title={refundActionText}></Button>
          </DropDown>
        ) : null}
      </div>

      <div className={css.columns}>
        <div className={css.column}>
          <PaymentField
            label={getI18n('allOrders.orderDetail.paymentHistory_gateway')}
            value={getGatewayLabel(item)}
          />
          <PaymentField
            label={getTimeLabel(
              'allOrders.orderDetail.paymentHistory_createdAt',
            )}
            value={paymentDetail?.createdAt || EMPTY_VALUE}
          />
          <PaymentField
            label={getTimeLabel('allOrders.orderDetail.paidat')}
            value={paymentDetail?.paidAt || EMPTY_VALUE}
          />
          <PaymentField
            label={getI18n('allOrders.orderDetail.externalReference')}
            value={paymentDetail?.gatewayReferenceId || EMPTY_VALUE}
          />
          {paymentDetail?.status === CustomerPaymentStatus.REFUNDED &&
          paymentDetail?.refundedAt ? (
            <PaymentField
              label={getTimeLabel(
                'allOrders.orderDetail.paymentHistory_refundedAt',
              )}
              value={paymentDetail.refundedAt}
            />
          ) : null}
        </div>

        <div className={css.column}>
          <PaymentField
            label={getI18n('general.status')}
            value={statusI18nKey ? getI18n(statusI18nKey) : EMPTY_VALUE}
          />
          <PaymentField
            label={getI18n('allOrders.orderDetail.paymentHistory_amount')}
            value={formatAmount(paymentDetail?.amount, decimalPosition)}
          />
          <PaymentField
            label={getI18n('general.currency')}
            value={paymentDetail?.currency || currencyCode || EMPTY_VALUE}
          />
          <PaymentField
            label={getI18n(
              'allOrders.orderDetail.paymentHistory_appliedToOrder',
            )}
            value={appliedI18nKey ? getI18n(appliedI18nKey) : EMPTY_VALUE}
          />
        </div>
      </div>
    </div>
  );
};

export default PaymentItem;
